/**
 * 🔍 USE DISCOVER HOOK - Architecture Mozart/Jobs/Tesla
 * 
 * Hook pour gérer la découverte et le swipe des profils.
 * 
 * PHILOSOPHIE HEARTBEATS:
 * - 🎭 Swipe sur avatars uniquement (dignité humaine)
 * - 📅 Maximum 3 nouvelles personnes par jour
 * - 💜 Qualité > Quantité
 */

import { useState, useCallback } from 'react';
import { api } from '../../services/api';
import { toast } from 'sonner';

export const useDiscover = (customerId) => {
  const [profiles, setProfiles] = useState([]);
  const [currentProfileIndex, setCurrentProfileIndex] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [swipeDirection, setSwipeDirection] = useState(null);
  const [filterVideoOnly, setFilterVideoOnly] = useState(false);
  
  // 📅 Limite quotidienne
  const [dailyLimit, setDailyLimit] = useState(3);
  const [remainingToday, setRemainingToday] = useState(3);

  const currentProfile = profiles[currentProfileIndex] || null;

  // Charger les profils
  const loadProfiles = useCallback(async (refresh = false, hasVideo = null) => {
    if (!customerId) return;
    
    const videoFilter = hasVideo !== null ? hasVideo : filterVideoOnly;
    
    setIsLoading(true);
    try {
      const data = await api.getHeartbeatsProfiles(customerId, videoFilter);
      // API returns { profiles: [...], dailyLimit, remainingToday }
      setProfiles(data?.profiles || data || []);
      
      // 📅 Mettre à jour la limite quotidienne
      if (data?.dailyLimit !== undefined) {
        setDailyLimit(data.dailyLimit);
      }
      if (data?.remainingToday !== undefined) {
        setRemainingToday(data.remainingToday);
      }
      
      // Message si limite atteinte
      if (data?.message) {
        toast.info(data.message, { duration: 5000 });
      }
      
      if (refresh) {
        setCurrentProfileIndex(0);
      }
    } catch (error) {
      console.error('Erreur chargement profils:', error);
      toast.error('Erreur lors du chargement des profils');
    } finally {
      setIsLoading(false);
    }
  }, [customerId, filterVideoOnly]);

  // Toggle filtre vidéo
  const toggleVideoFilter = useCallback((value) => {
    const newValue = value !== undefined ? value : !filterVideoOnly;
    setFilterVideoOnly(newValue);
    loadProfiles(true, newValue);
  }, [filterVideoOnly, loadProfiles]);

  // Swiper vers la droite (like) - devient "Tap pour discuter"
  const swipeRight = useCallback(async () => {
    if (!currentProfile) return;
    
    setSwipeDirection('right');
    try {
      const result = await api.sendHeartbeatsLike(customerId, currentProfile.customerId);
      if (result.isMatch) {
        toast.success("💥 C'est un MATCH ! Vous pouvez discuter !");
      } else {
        toast.success("💬 Tu veux discuter ! En attente de réponse...");
      }
      nextProfile();
    } catch (error) {
      toast.error("Erreur lors du like");
    } finally {
      setTimeout(() => setSwipeDirection(null), 300);
    }
  }, [customerId, currentProfile]);

  // Swiper vers la gauche (pass)
  const swipeLeft = useCallback(async () => {
    if (!currentProfile) return;
    
    setSwipeDirection('left');
    try {
      await api.sendHeartbeatsPass(customerId, currentProfile.customerId);
      nextProfile();
    } catch (error) {
      console.error('Erreur pass:', error);
    } finally {
      setTimeout(() => setSwipeDirection(null), 300);
    }
  }, [customerId, currentProfile]);

  // Profil suivant
  const nextProfile = useCallback(() => {
    setCurrentProfileIndex(prev => {
      const newIndex = prev + 1;
      if (newIndex >= profiles.length) {
        // Recharger les profils si on arrive à la fin
        loadProfiles(true);
        return 0;
      }
      return newIndex;
    });
  }, [profiles.length, loadProfiles]);

  // Profil précédent
  const prevProfile = useCallback(() => {
    setCurrentProfileIndex(prev => {
      const newIndex = prev - 1;
      return newIndex < 0 ? Math.max(profiles.length - 1, 0) : newIndex;
    });
  }, [profiles.length]);

  // Envoyer un super like
  const sendSuperLike = useCallback(async () => {
    if (!currentProfile) return;
    
    try {
      const result = await api.sendHeartbeatsSuperLike(customerId, currentProfile.customerId);
      if (result.isMatch) {
        toast.success("💥 SUPER MATCH !");
      } else {
        toast.success("⭐ Super Like envoyé !");
      }
      nextProfile();
    } catch (error) {
      toast.error("Erreur lors du Super Like");
    }
  }, [customerId, currentProfile, nextProfile]);

  // Envoyer un crush secret
  const sendCrush = useCallback(async () => {
    if (!currentProfile) return;
    
    try {
      const result = await api.sendHeartbeatsCrush(customerId, currentProfile.customerId);
      if (result.isMatch) {
        toast.success("💥 C'est un MATCH !");
      } else {
        toast.success("💘 Crush envoyé en secret !");
      }
      nextProfile();
    } catch (error) {
      toast.error("Erreur lors de l'envoi du crush");
    }
  }, [customerId, currentProfile, nextProfile]);

  return {
    profiles,
    currentProfile,
    currentProfileIndex,
    isLoading,
    swipeDirection,
    filterVideoOnly,
    loadProfiles,
    toggleVideoFilter,
    swipeRight,
    swipeLeft,
    nextProfile,
    prevProfile,
    sendSuperLike,
    sendCrush,
    // 📅 Limite quotidienne
    dailyLimit,
    remainingToday,
    // Aliases for DiscoverTab compatibility
    handleLike: swipeRight,
    handlePass: swipeLeft,
    handleSuperLike: sendSuperLike,
    showMatchAnimation: false,
    matchedProfile: null,
    closeMatchAnimation: () => {}
  };
};